import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Search, ChevronUp, ChevronDown, ChevronRight, ExternalLink, Lock, Paperclip, Play, EyeOff, Eye, FlaskConical, X as XIcon, Pencil } from 'lucide-react'
import {
  artifacts as artifactsApi,
  fetchArtifactResultDrift,
  manualEdit,
  matters as mattersApi,
  promptSlots as slotsApi,
  promptTestSets as testSetsApi,
  runPipelineBulk,
  type Artifact,
  type ArtifactDriftSummary,
  type PromptTestSet,
} from '../api'

type SortKey = 'source_date' | 'client_seq' | 'subject' | 'artifact_type' | 'source_type' | 'created_at'
type SortDir = 'asc' | 'desc'

const PAGE_SIZE = 50

// All filter/sort/pagination state lives in the URL via useSearchParams.
// That way the back button preserves filters when you drill into an
// artifact and return, and the URL is shareable.
function useUrlState() {
  const [params, setParams] = useSearchParams()
  const get = (k: string, d = '') => params.get(k) ?? d
  const set = (patch: Record<string, string | number | undefined>) => {
    const next = new URLSearchParams(params)
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined || v === '' || v === null) next.delete(k)
      else next.set(k, String(v))
    }
    // Mutating any filter resets pagination.
    if (Object.keys(patch).some((k) => k !== 'offset' && k !== 'sort' && k !== 'dir')) {
      next.delete('offset')
    }
    setParams(next, { replace: false })
  }
  return { get, set, raw: params }
}

// Hold the local input state separately from the URL so typing feels
// instant; only push to URL (= refetch) after the user pauses typing.
// 350ms is enough to coalesce a fast typist's burst without feeling
// laggy. Without this every keystroke fires a backend query that
// scans cached_content with `LIKE '%q%'` — sequential scan over
// every artifact body in the tenant. See models.py: no GIN/trigram
// index covers icontains on long text columns.
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delayMs)
    return () => window.clearTimeout(t)
  }, [value, delayMs])
  return debounced
}

export default function Artifacts() {
  const qc = useQueryClient()
  // Standardized on limit:500 + params-in-key so this query shares a
  // cache slot with Workspace and ArtifactViewer cleanly.
  const matters = useQuery({ queryKey: ['matters', { limit: 500 }], queryFn: () => mattersApi.list({ limit: 500 }) })
  const { get, set } = useUrlState()

  // `search` is what's in the URL (= what the backend filters on).
  // `searchInput` is what's in the textbox right now. They differ
  // mid-typing; the effect below pushes searchInput → URL after a pause.
  const search = get('search')
  const [searchInput, setSearchInput] = useState(search)
  const debouncedSearch = useDebouncedValue(searchInput, 350)
  // Avoid pushing the URL until the debounced value actually changes
  // from what's there — otherwise typing+matching same value re-renders.
  const lastPushedRef = useRef(search)
  useEffect(() => {
    if (debouncedSearch !== lastPushedRef.current) {
      lastPushedRef.current = debouncedSearch
      set({ search: debouncedSearch })
    }
    // `set` is stable per-render of useUrlState; safe to omit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])
  // If something else (like resetFilters or back/forward nav) changes
  // the URL `search` param, sync it back into the input.
  useEffect(() => {
    if (search !== searchInput && search !== debouncedSearch) {
      setSearchInput(search)
      lastPushedRef.current = search
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])
  const artifactType = get('type')
  const sourceType = get('source')
  const matterId = get('matter')
  const visibility = get('visibility')
  const hasResults = get('results')
  const ignoredFilter = get('ignored', 'false')  // false | true | any
  // is_personal: '' (any) | 'true' | 'false'. Filters the promoted column.
  const personalFilter = get('personal')
  // digest: '' (any) | 'true' | 'false'. Filters the promoted email_is_digest
  // column the "Classify Email as Personal, Digest" step writes. This is the
  // REAL digest filter — the structural=is_digest flag below is a different,
  // never-populated field (digests skip Split Email), so don't use that one.
  const digestFilter = get('digest')
  // structural: one email_structural flag name (e.g. 'is_forward'), or ''
  // for no filter. Backend accepts a comma-list (AND); UI picks one for now.
  const structuralFilter = get('structural')
  // Date-range filter on source_date (the column shown in the Date col).
  // Both sides optional. Inclusive on `from`, inclusive on `to` from the
  // user's perspective — backend translates `to` to "< to + 1 day".
  const dateFrom = get('date_from')
  const dateTo = get('date_to')
  // Default to sort-by-id (client_seq) descending — matches the user's
  // mental model that "the number conveys arrival order." source_date is
  // a worse default since attachments often have no source_date and fall
  // back to insertion order, which then *looks* like alphabetic-by-code
  // because each artifact type was imported as a batch.
  const sortKey = (get('sort', 'client_seq') as SortKey)
  const sortDir = (get('dir', 'desc') as SortDir)
  const offset = Number(get('offset', '0')) || 0
  // Nest = group attachments under their parent email. Honored even
  // during search (per Ken's request) so results stay structured;
  // tradeoff is that child-only matches are hidden under their parent
  // until expanded.
  const nestOn = get('nest', 'true') === 'true'
  const effectiveNest = nestOn

  // Per-row expand state (which parent rows have their children showing).
  const [expanded, setExpanded] = useState<Set<number>>(new Set())

  // Bulk-select state stays local; resets when filters change.
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [lastMsg, setLastMsg] = useState<string | null>(null)
  // Auto-dismiss the success chip so it doesn't linger between actions.
  // 6s gives enough time to read but doesn't haunt subsequent interactions.
  useEffect(() => {
    if (!lastMsg) return
    const t = window.setTimeout(() => setLastMsg(null), 6000)
    return () => window.clearTimeout(t)
  }, [lastMsg])
  const [addToTestSetOpen, setAddToTestSetOpen] = useState(false)

  const ordering = `${sortDir === 'desc' ? '-' : ''}${sortKey}`

  const page = useQuery({
    queryKey: ['artifactsPage', { search, artifactType, sourceType, matterId, visibility, hasResults, ignoredFilter, personalFilter, digestFilter, structuralFilter, dateFrom, dateTo, ordering, offset, effectiveNest }],
    queryFn: () =>
      artifactsApi.page({
        search,
        artifact_type: artifactType,
        source_type: sourceType,
        matter: matterId,
        visibility,
        has_results: hasResults,
        ignored: ignoredFilter,
        is_personal: personalFilter,
        email_is_digest: digestFilter,
        structural: structuralFilter,
        source_date_from: dateFrom,
        source_date_to: dateTo,
        ordering,
        limit: PAGE_SIZE,
        offset,
        // Top-level only when nest is on. Without `parent=null` the API
        // returns the flat list including attachments.
        parent: effectiveNest ? 'null' : undefined,
      }),
    // Hold the previous page's data while the next one fetches — avoids
    // the count blanking and the table going empty during paging.
    placeholderData: keepPreviousData,
  })

  const runBulk = useMutation({
    mutationFn: (ids: number[]) => runPipelineBulk(ids),
    onSuccess: (data) => {
      setLastMsg(`Enqueued ${data.enqueued}${data.rejected ? `, ${data.rejected} rejected` : ''}.`)
      setSelectedIds(new Set())
    },
  })
  const ignoreMu = useMutation({
    mutationFn: (ids: number[]) => artifactsApi.ignore(ids, 'manual'),
    onSuccess: (data) => {
      setLastMsg(`Ignored ${data.updated}.`)
      setSelectedIds(new Set())
      qc.invalidateQueries({ queryKey: ['artifactsPage'] })
    },
  })
  const unignoreMu = useMutation({
    mutationFn: (ids: number[]) => artifactsApi.unignore(ids),
    onSuccess: (data) => {
      setLastMsg(`Unignored ${data.updated}.`)
      setSelectedIds(new Set())
      qc.invalidateQueries({ queryKey: ['artifactsPage'] })
    },
  })

  function toggleSort(k: SortKey) {
    if (sortKey === k) {
      set({ dir: sortDir === 'asc' ? 'desc' : 'asc' })
    } else {
      const defaultDir = (k === 'subject' || k === 'artifact_type' || k === 'source_type') ? 'asc' : 'desc'
      set({ sort: k, dir: defaultDir })
    }
  }

  function resetFilters() {
    set({ search: '', type: '', source: '', matter: '', visibility: '', results: '', ignored: 'false', personal: '', digest: '', structural: '', date_from: '', date_to: '' })
  }

  const rows = page.data?.results ?? []
  const total = page.data?.count ?? 0
  const matterById = useMemo(() => new Map((matters.data ?? []).map((m) => [m.id, m])), [matters.data])

  // Per-row prompt-version drift (the chip rendered next to subject when
  // any of an artifact's step results were computed under a non-current
  // prompt version). Fetched in one go for the visible page.
  const visibleIds = useMemo(() => rows.map((r) => r.id), [rows])
  const driftQuery = useQuery({
    queryKey: ['artifact-result-drift', visibleIds.join(',')],
    queryFn: () => fetchArtifactResultDrift(visibleIds),
    enabled: visibleIds.length > 0,
    staleTime: 30_000,
  })
  const driftByArtifact = driftQuery.data?.by_artifact ?? {}

  const allChecked = rows.length > 0 && rows.every((r) => selectedIds.has(r.id))

  return (
    <div className="me-scroll flex h-full flex-col overflow-hidden">
      {addToTestSetOpen && (
        <AddToTestSetModal
          artifactIds={Array.from(selectedIds)}
          onClose={() => setAddToTestSetOpen(false)}
          onAdded={(name, n) => {
            setLastMsg(`Added ${n} artifact${n === 1 ? '' : 's'} to "${name}".`)
            setAddToTestSetOpen(false)
            setSelectedIds(new Set())
          }}
        />
      )}
      {/* Toolbar */}
      <div className="border-b me-border bg-[color:var(--me-surface)] p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[260px] max-w-[420px] flex-1">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 me-text-mute pointer-events-none" />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search code (EMAL-000042), subject, sender, content…"
              className="me-input !w-full !pl-9"
            />
          </div>
          <Pick value={artifactType} onChange={(v) => set({ type: v })}
            options={[['', 'all types'], ['email', 'email'], ['document', 'document'], ['attachment', 'attachment']]} />
          <Pick value={sourceType} onChange={(v) => set({ source: v })}
            options={[['', 'all sources'], ['gmail', 'gmail'], ['gdrive', 'gdrive'], ['upload', 'upload']]} />
          <Pick value={matterId} onChange={(v) => set({ matter: v })}
            options={[
              ['', 'any matter'],
              ...(matters.data ?? []).map<[string, string]>((m) => [String(m.id), m.name]),
            ]} />
          <Pick value={visibility} onChange={(v) => set({ visibility: v })}
            options={[['', 'any visibility'], ['shared', 'shared'], ['restricted', 'restricted']]} />
          <Pick value={hasResults} onChange={(v) => set({ results: v })}
            options={[['', 'any results'], ['any', 'has results'], ['none', 'no results'], ['stale', 'has stale']]} />
          <Pick value={ignoredFilter} onChange={(v) => set({ ignored: v })}
            options={[['false', 'hide ignored'], ['any', 'include ignored'], ['true', 'only ignored']]} />
          <Pick value={personalFilter} onChange={(v) => set({ personal: v })}
            options={[['', 'any personal'], ['true', 'personal only'], ['false', 'non-personal only']]} />
          {/* email_is_digest — the promoted column the "Classify Email as
              Personal, Digest" step writes. The real digest filter (the
              structural=is_digest flag is never set on actual digests). */}
          <Pick value={digestFilter} onChange={(v) => set({ digest: v })}
            options={[['', 'any digest'], ['true', 'digest only'], ['false', 'non-digest only']]} />
          {/* email_structural flags — emails the L2 step tagged. Single
              pick for now; backend `structural` param accepts a comma-list
              (AND) if we want multi-select later. */}
          <Pick value={structuralFilter} onChange={(v) => set({ structural: v })}
            options={[
              ['', 'any structure'],
              ['is_forward', 'forwarded'],
              ['copy_in', 'copy-in'],
              ['strictly_logistical', 'logistical only'],
              ['is_interlineated', 'interlineated'],
              ['legal_office_only', 'legal office only'],
              ['multiple_issues_detected', 'multiple issues'],
            ]} />
          <Pick value={String(nestOn)} onChange={(v) => set({ nest: v })}
            options={[['true', 'nest attachments'], ['false', 'flat list']]} />
          {/* Date range filter — operates on source_date (the column shown
              in the Date col). Browser-native date pickers; users on
              modern browsers get a calendar dropdown for free. Cleared
              by Reset. */}
          <label className="inline-flex items-center gap-1 text-[11px] me-text-mute">
            <span>from</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => set({ date_from: e.target.value })}
              className="me-input me-mono"
              style={{ minWidth: 130 }}
            />
          </label>
          <label className="inline-flex items-center gap-1 text-[11px] me-text-mute">
            <span>to</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => set({ date_to: e.target.value })}
              className="me-input me-mono"
              style={{ minWidth: 130 }}
            />
          </label>
          <button onClick={resetFilters} className="me-btn me-btn-outline">Reset</button>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-2 text-[12px] me-text-mute">
          {/* Count holds the placeholder value during paging — only shows
              "loading…" when there's truly no data yet (initial mount). */}
          <span className="me-mono">
            {page.data ? `${total} match${total === 1 ? '' : 'es'}` : 'loading…'}
          </span>
          <button
            onClick={() => page.refetch()}
            disabled={page.isFetching}
            className="me-btn me-btn-outline !text-[10.5px] !px-2 !py-1 inline-flex items-center gap-1"
            title="Refresh now (preserves search, filters, page, and selection)"
          >
            {page.isFetching ? '⟳ refreshing…' : '⟳ refresh'}
          </button>
          <span>·</span>
          <span className="me-mono">{selectedIds.size} selected</span>
          <button
            onClick={() => runBulk.mutate(Array.from(selectedIds))}
            disabled={selectedIds.size === 0 || runBulk.isPending}
            className="me-btn me-btn-primary"
          >
            <Play size={11} />
            {runBulk.isPending ? 'Enqueuing…' : 'Run pipeline on selected'}
          </button>
          <button
            onClick={() => ignoreMu.mutate(Array.from(selectedIds))}
            disabled={selectedIds.size === 0 || ignoreMu.isPending}
            className="me-btn me-btn-outline"
            title="Hide selected from default browser + skip in pipeline runs"
          >
            <EyeOff size={11} />
            Ignore
          </button>
          <button
            onClick={() => unignoreMu.mutate(Array.from(selectedIds))}
            disabled={selectedIds.size === 0 || unignoreMu.isPending}
            className="me-btn me-btn-outline"
          >
            <Eye size={11} />
            Unignore
          </button>
          <button
            onClick={() => setAddToTestSetOpen(true)}
            disabled={selectedIds.size === 0}
            className="me-btn me-btn-outline"
            title="Add selected artifacts to a prompt test set (for iterating prompts in Studio)"
          >
            <FlaskConical size={11} />
            Add to test set…
          </button>
          {lastMsg && <span className="me-chip me-chip-fresh">{lastMsg}</span>}
        </div>
      </div>

      {/* Table */}
      <div className="me-scroll relative flex-1 overflow-auto">
        {/* Loading overlay — shows while any backend fetch is in flight,
            including search-driven refetches (placeholderData covers
            paging only since the queryKey changes when search changes).
            Keeps previous rows visible underneath at reduced opacity so
            the user sees what they had before — same pattern as the
            Workspace matter timeline. */}
        {page.isFetching && (
          <div
            className="pointer-events-none absolute inset-0 z-20 flex items-start justify-center pt-12"
            aria-label="Loading"
            role="status"
          >
            <div className="size-8 rounded-full border-[3px] border-[color:var(--me-textMute)] border-t-transparent animate-spin" />
          </div>
        )}
        <table className={`w-full text-[12.5px] transition-opacity ${page.isFetching ? 'opacity-50' : ''}`}>
          <thead className="sticky top-0 bg-[color:var(--me-surface)] z-10">
            <tr className="border-b me-border">
              <th className="px-2 py-1.5 text-left">
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={async () => {
                    if (allChecked) {
                      // Unchecking: drop the visible parents from selection.
                      // We don't try to drop children — they may have been
                      // selected individually before; user can clear those.
                      const next = new Set(selectedIds)
                      for (const r of rows) next.delete(r.id)
                      setSelectedIds(next)
                      return
                    }
                    // Checking: select parents immediately for snappy UX.
                    setSelectedIds(prev => {
                      const next = new Set(prev)
                      for (const r of rows) next.add(r.id)
                      return next
                    })
                    // Auto-expand parents that have children, so the user
                    // SEES the about-to-be-selected attachments visually.
                    const parentsWithKids = rows.filter(
                      (r) => r.children_count && r.children_count.visible > 0,
                    )
                    if (parentsWithKids.length === 0) return
                    setExpanded(prev => {
                      const e = new Set(prev)
                      for (const r of parentsWithKids) e.add(r.id)
                      return e
                    })
                    // Fetch children IDs in parallel and add to selection.
                    // Excludes ignored children (they'd be skipped in
                    // pipeline runs anyway — keeps the selection list
                    // matched to what'll actually run).
                    try {
                      const results = await Promise.all(
                        parentsWithKids.map((r) =>
                          artifactsApi.page({ parent: r.id, ignored: 'false', limit: 100 }),
                        ),
                      )
                      const childIds = results.flatMap((p) => p.results.map((c) => c.id))
                      if (childIds.length > 0) {
                        setSelectedIds(prev => {
                          const next = new Set(prev)
                          for (const cid of childIds) next.add(cid)
                          return next
                        })
                      }
                    } catch (e) {
                      console.error('select-all: failed to load attachment ids', e)
                    }
                  }}
                />
              </th>
              {effectiveNest && <th className="w-10 px-1 py-1.5"></th>}
              <SortHeader col="client_seq" label="code" {...{ sortKey, sortDir, toggleSort }} />
              <SortHeader col="artifact_type" label="type" {...{ sortKey, sortDir, toggleSort }} />
              <SortHeader col="source_type" label="source" {...{ sortKey, sortDir, toggleSort }} />
              <SortHeader col="subject" label="subject" {...{ sortKey, sortDir, toggleSort }} grow />
              <th className="px-2 py-1.5 text-left me-mono text-[11px] me-text-mute uppercase">from</th>
              <SortHeader col="source_date" label="date" {...{ sortKey, sortDir, toggleSort }} />
              <th className="px-2 py-1.5 text-left me-mono text-[11px] me-text-mute uppercase">matters</th>
              <th className="px-2 py-1.5 text-left me-mono text-[11px] me-text-mute uppercase">flags</th>
              <th className="px-2 py-1.5 text-center me-mono text-[11px] me-text-mute uppercase" title="Ignore / unignore">ign</th>
              <th className="px-2 py-1.5 text-right me-mono text-[11px] me-text-mute uppercase">view</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => {
              const isExpanded = expanded.has(a.id)
              return (
                <>
                  <Row
                    key={a.id}
                    a={a}
                    picked={selectedIds.has(a.id)}
                    matterName={(id: number) => matterById.get(id)?.name}
                    nestEnabled={effectiveNest}
                    isExpanded={isExpanded}
                    drift={driftByArtifact[String(a.id)]}
                    onToggleExpand={() => {
                      const next = new Set(expanded)
                      if (next.has(a.id)) next.delete(a.id); else next.add(a.id)
                      setExpanded(next)
                    }}
                    onTogglePick={() => {
                      const next = new Set(selectedIds)
                      if (next.has(a.id)) next.delete(a.id); else next.add(a.id)
                      setSelectedIds(next)
                    }}
                    onToggleIgnore={() => {
                      if (a.ignored) unignoreMu.mutate([a.id])
                      else ignoreMu.mutate([a.id])
                    }}
                    ignoreActionPending={ignoreMu.isPending || unignoreMu.isPending}
                  />
                  {effectiveNest && isExpanded && (
                    <ChildRows
                      key={`children-${a.id}`}
                      parentId={a.id}
                      ignoredFilter={ignoredFilter}
                      ordering={ordering}
                      matterName={(id: number) => matterById.get(id)?.name}
                      selectedIds={selectedIds}
                      onTogglePick={(cid) => {
                        const next = new Set(selectedIds)
                        if (next.has(cid)) next.delete(cid); else next.add(cid)
                        setSelectedIds(next)
                      }}
                      onToggleIgnore={(child) => {
                        if (child.ignored) unignoreMu.mutate([child.id])
                        else ignoreMu.mutate([child.id])
                      }}
                      ignoreActionPending={ignoreMu.isPending || unignoreMu.isPending}
                    />
                  )}
                </>
              )
            })}
            {!page.isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={11} className="px-3 py-10 text-center text-[12px] me-text-mute italic">
                  No artifacts match these filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination — pr-14 leaves room for the floating Tweaks gear in the
          bottom-right corner so the rightmost button isn't hidden underneath it. */}
      <div className="border-t me-border bg-[color:var(--me-surface)] px-3 pr-14 py-1.5 text-[12px] me-text-mute flex items-center justify-end gap-2">
        {(() => {
          const pageNum = Math.floor(offset / PAGE_SIZE) + 1
          const lastPage = total === 0 ? 1 : Math.ceil(total / PAGE_SIZE)
          const jump = (delta: number) => {
            const target = Math.max(0, Math.min((lastPage - 1) * PAGE_SIZE, offset + delta * PAGE_SIZE))
            set({ offset: target })
          }
          return (
            <>
              <span className="me-mono">
                {total === 0 ? '0' : `${offset + 1}–${Math.min(offset + PAGE_SIZE, total)}`} of {total}
                <span className="ml-2 me-text-mute">· page {pageNum} of {lastPage}</span>
              </span>
              <button
                disabled={offset === 0}
                onClick={() => jump(-10)}
                className="me-btn me-btn-outline"
                title="Back 10 pages"
              >−10</button>
              <button
                disabled={offset === 0}
                onClick={() => jump(-1)}
                className="me-btn me-btn-outline"
              >prev</button>
              <button
                disabled={!page.data?.next}
                onClick={() => jump(1)}
                className="me-btn me-btn-outline"
              >next</button>
              <button
                disabled={pageNum >= lastPage}
                onClick={() => jump(10)}
                className="me-btn me-btn-outline"
                title="Forward 10 pages"
              >+10</button>
            </>
          )
        })()}
      </div>
    </div>
  )
}

function Pick({ value, onChange, options, width = 'w-36' }: {
  value: string
  onChange: (v: string) => void
  options: [string, string][]
  width?: string
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`me-input ${width} !w-auto min-w-[110px] max-w-[180px]`}
    >
      {options.map(([v, l]) => (
        <option key={v} value={v}>{l}</option>
      ))}
    </select>
  )
}

function SortHeader({
  col, label, sortKey, sortDir, toggleSort, grow,
}: {
  col: SortKey
  label: string
  sortKey: SortKey
  sortDir: SortDir
  toggleSort: (k: SortKey) => void
  grow?: boolean
}) {
  const active = sortKey === col
  return (
    <th className={`px-2 py-1.5 text-left ${grow ? 'w-full' : ''}`}>
      <button
        onClick={() => toggleSort(col)}
        className="me-mono inline-flex items-center gap-0.5 text-[11px] uppercase tracking-wider me-text-mute hover:me-text"
      >
        {label}
        {active && (sortDir === 'asc' ? <ChevronUp size={10} /> : <ChevronDown size={10} />)}
      </button>
    </th>
  )
}

function Row({
  a, picked, matterName, onTogglePick, onToggleIgnore, ignoreActionPending,
  nestEnabled, isExpanded, onToggleExpand, isChild, drift,
}: {
  a: Artifact
  picked: boolean
  matterName: (id: number) => string | undefined
  onTogglePick: () => void
  onToggleIgnore: () => void
  ignoreActionPending: boolean
  nestEnabled?: boolean
  isExpanded?: boolean
  onToggleExpand?: () => void
  isChild?: boolean
  drift?: ArtifactDriftSummary
}) {
  const cc = a.children_count
  const canExpand = nestEnabled && !isChild && cc.total > 0
  // Outdated/stale step counts for the drift chip rendered next to subject.
  // Both are derived; hide the chip when there's nothing interesting.
  const outdatedSteps = drift
    ? (drift.computation_result.outdated + drift.matter_step_result.outdated)
    : 0
  const staleSteps = drift
    ? (drift.computation_result.stale + drift.matter_step_result.stale)
    : 0
  const driftStepNames = (drift?.steps ?? [])
    .filter((s) => s.is_outdated || s.is_stale)
    .slice(0, 6)
    .map((s) => `${s.step_name}${s.is_stale ? ' [stale]' : ''}${s.is_outdated ? ' [outdated]' : ''}`)
    .join('\n')
  // "visible / total" when some children are filtered out (auto-ignored).
  // Plain count when all children are visible.
  const childLabel = cc.visible === cc.total ? `${cc.total}` : `${cc.visible}/${cc.total}`
  return (
    <tr
      className={`border-b me-border hover:bg-[color:var(--me-surfaceHover)] ${a.ignored ? 'opacity-60' : ''} ${isChild ? 'bg-[color:var(--me-bg)]' : ''}`}
    >
      <td className="px-2 py-1.5">
        <input type="checkbox" checked={picked} onChange={onTogglePick} />
      </td>
      {nestEnabled && (
        <td className="w-10 px-1 py-1.5 align-middle">
          {canExpand ? (
            <button
              onClick={onToggleExpand}
              className="inline-flex items-center gap-0.5 me-text hover:font-semibold"
              title={isExpanded
                ? 'Collapse children'
                : `Expand ${cc.total} attachment${cc.total === 1 ? '' : 's'}${cc.visible < cc.total ? ` (${cc.total - cc.visible} ignored)` : ''}`}
            >
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              <span className={`me-mono text-[10px] ${cc.visible === 0 ? 'me-text-mute italic' : 'me-text-mute'}`}>
                {childLabel}
              </span>
            </button>
          ) : isChild ? (
            <span className="me-text-mute" title="attachment of parent above">
              <Paperclip size={10} />
            </span>
          ) : null}
        </td>
      )}
      <td className={`px-2 py-1.5 me-mono text-[11.5px] me-text-mute whitespace-nowrap ${isChild ? 'pl-6' : ''}`} title={`internal id ${a.id}`}>
        {a.composite_id}
      </td>
      <td className="px-2 py-1.5">
        <span className="me-chip uppercase">{a.artifact_type}</span>
        {a.parent_artifact && (
          <Paperclip size={10} className="ml-1 inline me-text-mute" />
        )}
      </td>
      <td className="px-2 py-1.5 me-mono text-[11.5px] me-text-mute">{a.source_type}</td>
      <td className="px-2 py-1.5">
        <div className="flex items-center gap-1.5">
          <SubjectCell artifactId={a.id} subject={a.subject} />
          {(outdatedSteps > 0 || staleSteps > 0) && (
            <span
              className="me-chip"
              style={{ background: 'var(--me-staleBg)', color: 'var(--me-stale)', fontSize: '10px' }}
              title={
                `${outdatedSteps} step result${outdatedSteps === 1 ? '' : 's'} on an old prompt version · `
                + `${staleSteps} stale (queued for rerun)\n\n`
                + (driftStepNames || '(no per-step detail)')
              }
            >
              {outdatedSteps > 0 ? `outdated: ${outdatedSteps}` : `stale: ${staleSteps}`}
            </span>
          )}
        </div>
      </td>
      <td className="px-2 py-1.5 me-mono text-[11px] me-text-dim truncate max-w-[160px]">
        {a.from_name || a.from_email || '—'}
      </td>
      <td className="px-2 py-1.5 me-mono text-[11px] me-text-dim whitespace-nowrap">
        {a.source_date ? new Date(a.source_date).toLocaleDateString() : '—'}
      </td>
      <td className="px-2 py-1.5 text-[11px] me-text-dim truncate max-w-[180px]">
        {a.matter_ids.length === 0
          ? '—'
          : a.matter_ids.slice(0, 2).map((id) => matterName(id) ?? `#${id}`).join(', ')
            + (a.matter_ids.length > 2 ? ` +${a.matter_ids.length - 2}` : '')}
      </td>
      <td className="px-2 py-1.5">
        {a.visibility === 'restricted' && (
          <span className="me-chip me-chip-stale" title="Restricted visibility">
            <Lock size={10} />
          </span>
        )}
        {a.ignored && (
          <span className="me-chip me-chip-stale ml-0.5" title={a.ignored_reason || 'ignored'}>
            <EyeOff size={10} />
          </span>
        )}
      </td>
      <td className="px-2 py-1.5 text-center">
        <button
          onClick={onToggleIgnore}
          disabled={ignoreActionPending}
          className="me-btn me-btn-outline"
          title={a.ignored ? `Unignore (currently: ${a.ignored_reason || 'manual'})` : 'Ignore — hide from default browser, skip in pipeline runs'}
        >
          {a.ignored ? <Eye size={11} /> : <EyeOff size={11} />}
        </button>
      </td>
      <td className="px-2 py-1.5 text-right">
        <Link to={`/studio/artifact/${a.id}`} className="me-btn me-btn-outline" title="Open viewer">
          <ExternalLink size={11} />
        </Link>
      </td>
    </tr>
  )
}

// SubjectCell: by default shows the subject as a link to the artifact
// viewer (preserves the dominant click-to-open affordance). A small
// pencil at top-right of the cell switches to an inline editor that
// saves via /api/manual-edit/. Editing doesn't navigate — the link is
// hidden during edit mode.
function SubjectCell({ artifactId, subject }: { artifactId: number; subject: string }) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(subject || '')
  const [err, setErr] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: () => manualEdit({
      target_type: 'artifact', target_id: artifactId,
      field: 'subject', value: draft.trim(),
    }),
    onSuccess: () => {
      setEditing(false)
      setErr(null)
      qc.invalidateQueries({ queryKey: ['artifact', artifactId] })
      qc.invalidateQueries({ queryKey: ['artifactsPage'] })
    },
    onError: (e) => setErr((e as Error).message),
  })
  if (editing) {
    return (
      <div className="flex items-center gap-1 max-w-[460px]">
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') { e.preventDefault(); setEditing(false); setDraft(subject || '') }
            if (e.key === 'Enter') {
              e.preventDefault()
              if (draft.trim() && draft.trim() !== subject) save.mutate()
              else setEditing(false)
            }
          }}
          maxLength={998}
          className="me-input flex-1 !text-[12px] !py-1"
        />
        <button
          onClick={() => save.mutate()}
          disabled={save.isPending || !draft.trim() || draft.trim() === subject}
          className="me-btn me-btn-primary !text-[10px] !px-2 !py-1"
          title="Save (Enter)"
        >
          {save.isPending ? '…' : 'Save'}
        </button>
        <button
          onClick={() => { setEditing(false); setDraft(subject || ''); setErr(null) }}
          className="me-btn me-btn-outline !text-[10px] !px-2 !py-1"
          title="Cancel (Esc)"
        >
          Cancel
        </button>
        {err && <span className="text-[10.5px] text-[color:var(--me-stale)] truncate max-w-[160px]" title={err}>{err}</span>}
      </div>
    )
  }
  return (
    <div className="group relative inline-flex items-center gap-1 max-w-[420px]">
      <Link
        to={`/studio/artifact/${artifactId}`}
        className="font-medium me-text hover:underline truncate block max-w-[380px]"
      >
        {subject || '(no subject)'}
      </Link>
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditing(true) }}
        title="Edit subject"
        type="button"
        className="me-text-mute hover:me-text"
        style={{ opacity: 0.28 }}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = '1' }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.28' }}
      >
        <Pencil size={11} />
      </button>
    </div>
  )
}


// ChildRows: lazily fetches and renders the attachments of a single parent
// inline as <tr> children. Inherits the parent table's sort + ignored filter
// so the children read consistently with what's around them.
function ChildRows({
  parentId, ignoredFilter, ordering, matterName, selectedIds, onTogglePick, onToggleIgnore, ignoreActionPending,
}: {
  parentId: number
  ignoredFilter: string
  ordering: string
  matterName: (id: number) => string | undefined
  selectedIds: Set<number>
  onTogglePick: (id: number) => void
  onToggleIgnore: (a: Artifact) => void
  ignoreActionPending: boolean
}) {
  const children = useQuery({
    queryKey: ['artifactChildren', parentId, ignoredFilter, ordering],
    // Always include ignored — the parent toolbar filter applies to which
    // emails are visible, but once you expand a parent you want to see its
    // attachments regardless of ignore status (ignored ones render dimmed).
    queryFn: () => artifactsApi.page({
      parent: parentId, ignored: 'any', ordering, limit: 100,
    }),
  })

  if (children.isLoading) {
    return (
      <tr>
        <td colSpan={11} className="px-3 py-1 text-[11px] me-text-mute italic">
          loading attachments…
        </td>
      </tr>
    )
  }
  const rows = children.data?.results ?? []
  if (rows.length === 0) {
    return (
      <tr>
        <td colSpan={11} className="px-3 py-1 text-[11px] me-text-mute italic">
          no attachments
        </td>
      </tr>
    )
  }
  return (
    <>
      {rows.map((c) => (
        <Row
          key={c.id}
          a={c}
          picked={selectedIds.has(c.id)}
          matterName={matterName}
          nestEnabled
          isChild
          onTogglePick={() => onTogglePick(c.id)}
          onToggleIgnore={() => onToggleIgnore(c)}
          ignoreActionPending={ignoreActionPending}
        />
      ))}
    </>
  )
}

// AddToTestSetModal: pick (or create) a test set, add the selected artifacts to it.
// Test sets are scoped to a slot — pick the slot first, then a set under that slot.
// We don't merge tags or do dedup ourselves; the API call passes the union of
// existing artifacts + selected, server-side m2m handles duplicates.
function AddToTestSetModal({
  artifactIds, onClose, onAdded,
}: {
  artifactIds: number[]
  onClose: () => void
  onAdded: (testSetName: string, n: number) => void
}) {
  const slots = useQuery({ queryKey: ['promptSlots'], queryFn: () => slotsApi.list() })
  const [slotId, setSlotId] = useState<number | ''>('')
  const sets = useQuery({
    queryKey: ['promptTestSets', slotId],
    queryFn: () => (slotId ? testSetsApi.bySlot(Number(slotId)) : Promise.resolve([])),
    enabled: !!slotId,
  })
  const [selectedSetId, setSelectedSetId] = useState<number | ''>('')
  const [creatingNew, setCreatingNew] = useState(false)
  const [newName, setNewName] = useState('')

  const add = useMutation({
    mutationFn: async () => {
      let target: PromptTestSet
      if (creatingNew) {
        if (!newName.trim() || !slotId) throw new Error('name + slot required')
        target = await testSetsApi.create({
          slot: Number(slotId), name: newName.trim(),
          artifacts: artifactIds,
        })
        return target
      }
      if (!selectedSetId) throw new Error('pick a test set')
      const existing = (sets.data ?? []).find((s) => s.id === Number(selectedSetId))!
      const merged = Array.from(new Set([...existing.artifacts, ...artifactIds]))
      return testSetsApi.update(Number(selectedSetId), { artifacts: merged })
    },
    onSuccess: (ts) => {
      onAdded(ts.name, artifactIds.length)
    },
  })

  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-black/30 p-6">
      <div className="me-card w-full max-w-md p-5">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-[14px] font-semibold me-text">
            Add {artifactIds.length} artifact{artifactIds.length === 1 ? '' : 's'} to test set
          </div>
          <button onClick={onClose} className="me-btn me-btn-outline" title="Close">
            <XIcon size={11} />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <div className="me-section-h !mb-1">Slot</div>
            <select
              value={slotId}
              onChange={(e) => {
                setSlotId(e.target.value ? Number(e.target.value) : '')
                setSelectedSetId('')
                setCreatingNew(false)
              }}
              className="me-input w-full"
            >
              <option value="">choose…</option>
              {(slots.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>L{s.level} {s.name}</option>
              ))}
            </select>
          </div>

          {!!slotId && !creatingNew && (
            <div>
              <div className="me-section-h !mb-1">Test set</div>
              <select
                value={selectedSetId}
                onChange={(e) => setSelectedSetId(e.target.value ? Number(e.target.value) : '')}
                className="me-input w-full"
              >
                <option value="">choose…</option>
                {(sets.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>{s.name} ({s.artifact_count} artifacts)</option>
                ))}
              </select>
              <button
                onClick={() => { setCreatingNew(true); setSelectedSetId('') }}
                className="mt-2 text-[11px] me-text-mute hover:me-text underline"
              >
                or create a new test set
              </button>
            </div>
          )}

          {!!slotId && creatingNew && (
            <div>
              <div className="me-section-h !mb-1">New test set name</div>
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. baseline 10 emails"
                className="me-input w-full"
              />
              <button
                onClick={() => { setCreatingNew(false); setNewName('') }}
                className="mt-2 text-[11px] me-text-mute hover:me-text underline"
              >
                or pick an existing one
              </button>
            </div>
          )}

          {add.error && (
            <div className="text-[11px] text-[color:var(--me-stale)]">
              {(add.error as Error).message}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="me-btn me-btn-outline">Cancel</button>
            <button
              onClick={() => add.mutate()}
              disabled={
                !slotId ||
                add.isPending ||
                (creatingNew ? !newName.trim() : !selectedSetId)
              }
              className="me-btn me-btn-primary"
            >
              {add.isPending ? 'Adding…' : (creatingNew ? 'Create + add' : 'Add')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
